import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  input,
  InputSignal,
  NgZone,
  OnDestroy,
  signal,
  Signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { SimpleMatTooltipComponent } from '../../../simple-mat-tooltip';
import { Observable } from 'rxjs';
import { ChartItem } from 'chart.js';
import { GeoChartJsonService } from '../../services';
import { TOOLTIP_AUTO_HIDE_TIMEOUT } from '../../configs';
import { Topology } from 'topojson-specification';
import { ChoroplethChart } from 'chartjs-chart-geo';
import { UntilDestroy } from '@ngneat/until-destroy';
import { toSignal } from '@angular/core/rxjs-interop';

@UntilDestroy()
@Component({
    selector: 'app-map-chart-base',
    template: '',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export abstract class MapChartBaseComponent implements OnDestroy {
  protected geoChartJsonService: GeoChartJsonService = inject(GeoChartJsonService);
  protected ngZone: NgZone = inject(NgZone);

  postfix: InputSignal<string> = input<string>('');
  data: InputSignal<number[]> = input.required<number[]>();
  labels: InputSignal<string[]> = input.required<string[]>();

  topology: Signal<Topology> = toSignal(this.getTopology(), { initialValue: null });

  canvas: Signal<ElementRef<HTMLCanvasElement>> = viewChild.required('canvas');
  tooltip: Signal<SimpleMatTooltipComponent> = viewChild.required('tooltip');

  isCanvasVisible: WritableSignal<boolean> = signal(false);

  readonly tooltipAutoHideTimeout: number = TOOLTIP_AUTO_HIDE_TIMEOUT;

  readonly minAvailableColorsCount: number = 20;

  protected chart: ChoroplethChart = null;

  constructor() {
    this.init();
  }

  private init(): void {
    effect(() => {
      const postfix: string = this.postfix();
      const data: number[] = this.data();
      const labels: string[] = this.labels();
      const topology: Topology = this.topology();

      if (postfix && data && labels && topology) {
          this._createMap(data, labels, postfix, topology)
      }
    });
  }

  private _createMap(data: number[], labels: string[], postfix: string, topology: Topology): void {
    const canvas: HTMLCanvasElement = this.canvas().nativeElement;
    const context: ChartItem = canvas.getContext('2d');

    this.destroyChart();

    this.ngZone.runOutsideAngular(() => this.createChart(context, data, labels, postfix, topology));

    this.isCanvasVisible.set(true);
  }

  ngOnDestroy(): void {
    this.destroyChart();
  }

  protected destroyChart() {
    this.isCanvasVisible.set(false);

    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }

  protected calcMaxAvailableColors(values: number[]){
    let minvalue: number = Math.min(...values) || 1;
    let maxvalue: number = Math.max(...values) || 1;

    return Math.max(Math.trunc(maxvalue/minvalue) + 1, this.minAvailableColorsCount);
  }

  protected abstract getTopology(): Observable<Topology>;

  protected abstract createChart(context: ChartItem, data: number[], labels: string[], postfix: string, topology: Topology): ChoroplethChart;
}
